import { memo, useState, useCallback } from 'react';
import {
  Button,
  Input,
  Label,
  Separator,
  Checkbox,
} from '@flote/ui';
import { Loader2, Building2, MapPin, Search, Users } from 'lucide-react';
import { useGooglePlaces, type ParsedAddress } from '@flote/app-core';
import type { CreateHubRequest, Team } from '@flote/contracts/http';

export type HubFormData = CreateHubRequest;

interface HubFormProps {
  onSubmit: (data: HubFormData) => Promise<void>;
  isProcessing?: boolean;
  teams: Team[];
  initialData?: Partial<HubFormData & { id?: string }>;
  mode?: 'create' | 'edit';
}

export const HubForm = memo<HubFormProps>(({
  onSubmit,
  isProcessing = false,
  teams,
  initialData,
  mode = 'create',
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [name, setName] = useState(initialData?.name || '');
  const [selectedTeams, setSelectedTeams] = useState<string[]>(initialData?.teams || []);

  // Address search field (full formatted address)
  const [addressSearch, setAddressSearch] = useState('');

  // Parsed address fields
  const [streetNumber, setStreetNumber] = useState(initialData?.address?.number || '');
  const [streetAddress, setStreetAddress] = useState(initialData?.address?.street || '');
  const [apartment, setApartment] = useState(initialData?.address?.apartment || '');
  const [city, setCity] = useState(initialData?.address?.city || '');
  const [state, setState] = useState(initialData?.address?.state || '');
  const [postalCode, setPostalCode] = useState(initialData?.address?.postalCode || '');
  const [country, setCountry] = useState(initialData?.address?.country || 'USA');

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Handle address selection from Google Places autocomplete
  const handlePlaceSelect = useCallback((address: ParsedAddress) => {
    // Set the full formatted address in the search field
    setAddressSearch(address.formattedAddress);
    // Populate all parsed fields
    setStreetNumber(address.number);
    setStreetAddress(address.street);
    setApartment(address.apartment);
    setCity(address.city);
    setState(address.state);
    setPostalCode(address.postalCode);
    setCountry(address.country || 'USA');
    // Clear any address-related errors when auto-filled
    setErrors((prev) => {
      const next = { ...prev };
      delete next.addressSearch;
      delete next.streetNumber;
      delete next.streetAddress;
      delete next.city;
      delete next.country;
      return next;
    });
  }, []);

  // Google Places autocomplete hook - connected to the search input
  const { inputRef: addressSearchRef } = useGooglePlaces(
    import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    { onPlaceSelect: handlePlaceSelect }
  );

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Hub name is required';
    }

    // Onfleet requires: number, street, city, country for parsed addresses
    if (!streetNumber.trim()) {
      newErrors.streetNumber = 'Street number is required';
    }
    if (!streetAddress.trim()) {
      newErrors.streetAddress = 'Street name is required';
    }
    if (!city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!country.trim()) {
      newErrors.country = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildHubRequest = (): HubFormData => {
    return {
      name: name.trim(),
      address: {
        number: streetNumber.trim() || undefined,
        street: streetAddress.trim() || undefined,
        apartment: apartment.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim().toUpperCase() || undefined,
        postalCode: postalCode.trim() || undefined,
        country: country.trim() || undefined,
      },
      teams: selectedTeams,
    };
  };

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeams(prev =>
      prev.includes(teamId)
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const hubRequest = buildHubRequest();
      await onSubmit(hubRequest);
    } finally {
      setIsSubmitting(false);
    }
  }, [name, streetNumber, streetAddress, apartment, city, state, postalCode, country, selectedTeams, onSubmit]);

  const processing = isSubmitting || isProcessing;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Hub Name Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          Hub Information
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Hub Name *</Label>
          <Input
            id="name"
            placeholder="e.g., Downtown Distribution Center"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={processing}
            className={errors.name ? 'border-destructive' : ''}
          />
          {errors.name && (
            <p className="text-xs text-destructive">{errors.name}</p>
          )}
        </div>
      </div>

      <Separator />

      {/* Hub Address Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          Hub Location
        </div>

        {/* Address Search with Autocomplete */}
        <div className="space-y-2">
          <Label htmlFor="addressSearch">Search Address</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={addressSearchRef}
              id="addressSearch"
              placeholder="Start typing to search for an address..."
              value={addressSearch}
              onChange={(e) => setAddressSearch(e.target.value)}
              disabled={processing}
              className={`pl-9 ${errors.addressSearch ? 'border-destructive' : ''}`}
              autoComplete="off"
            />
          </div>
          {errors.addressSearch && (
            <p className="text-xs text-destructive">{errors.addressSearch}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Search for an address to auto-fill the fields below
          </p>
        </div>

        {/* Parsed Address Fields */}
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-4">
          <div className="space-y-2">
            <Label htmlFor="streetNumber">Street Number *</Label>
            <Input
              id="streetNumber"
              placeholder="123"
              value={streetNumber}
              onChange={(e) => setStreetNumber(e.target.value)}
              disabled={processing}
              className={errors.streetNumber ? 'border-destructive' : ''}
            />
            {errors.streetNumber && (
              <p className="text-xs text-destructive">{errors.streetNumber}</p>
            )}
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="streetAddress">Street Name *</Label>
            <Input
              id="streetAddress"
              placeholder="Main Street"
              value={streetAddress}
              onChange={(e) => setStreetAddress(e.target.value)}
              disabled={processing}
              className={errors.streetAddress ? 'border-destructive' : ''}
            />
            {errors.streetAddress && (
              <p className="text-xs text-destructive">{errors.streetAddress}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="apartment">Apt / Suite</Label>
            <Input
              id="apartment"
              placeholder="Unit 4B"
              value={apartment}
              onChange={(e) => setApartment(e.target.value)}
              disabled={processing}
            />
          </div>
        </div>

        <div className="grid gap-4 grid-cols-2 sm:grid-cols-4">
          <div className="space-y-2">
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              placeholder="Los Angeles"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={processing}
              className={errors.city ? 'border-destructive' : ''}
            />
            {errors.city && (
              <p className="text-xs text-destructive">{errors.city}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="state">State</Label>
            <Input
              id="state"
              placeholder="CA"
              value={state}
              onChange={(e) => setState(e.target.value.toUpperCase())}
              disabled={processing}
              maxLength={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="postalCode">Postal Code</Label>
            <Input
              id="postalCode"
              placeholder="90001"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              disabled={processing}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="country">Country *</Label>
            <Input
              id="country"
              placeholder="USA"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              disabled={processing}
              className={errors.country ? 'border-destructive' : ''}
            />
            {errors.country && (
              <p className="text-xs text-destructive">{errors.country}</p>
            )}
          </div>
        </div>
      </div>

      <Separator />

      {/* Team Assignment Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Users className="h-4 w-4 text-muted-foreground" />
          Team Assignment
          <span className="text-xs font-normal text-muted-foreground">(optional)</span>
        </div>

        {teams.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No teams available. Create a team first.
          </p>
        ) : (
          <div className="space-y-2">
            <div className="grid gap-2">
              {teams.map((team) => (
                <div key={team.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`team-${team.id}`}
                    checked={selectedTeams.includes(team.id)}
                    onCheckedChange={() => handleTeamToggle(team.id)}
                    disabled={processing}
                  />
                  <Label
                    htmlFor={`team-${team.id}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {team.name}
                  </Label>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Select teams that operate from this hub
            </p>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex pt-2">
        <Button type="submit" disabled={processing} className="w-full">
          {processing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {mode === 'create' ? 'Creating Hub...' : 'Updating Hub...'}
            </>
          ) : (
            mode === 'create' ? 'Create Hub' : 'Update Hub'
          )}
        </Button>
      </div>
    </form>
  );
});

HubForm.displayName = 'HubForm';
